
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Loader2 } from "lucide-react";
import { useUsuarios, type Usuario } from "@/hooks/useUsuarios";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getRoleDisplayName, getRoleBadgeColor, UserRole } from "@/types/auth";
import { useAuth } from "@/hooks/useAuth";
import { EditarUsuarioDialog } from "./EditarUsuarioDialog";
import { RedefinirSenhaDialog } from "./RedefinirSenhaDialog";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

type SortField = "nome" | "email" | "role" | "setor" | "status" | "created_at";
type SortDirection = "asc" | "desc";

function getSetorBadgeClass(setor?: string) {
  switch (setor) {
    case "vendas":
      return "bg-green-100 text-green-800";
    case "financeiro":
      return "bg-blue-100 text-blue-800";
    case "tecnico":
      return "bg-purple-100 text-purple-800";
    case "administrativo":
      return "bg-yellow-100 text-yellow-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

function getSetorLabel(setor?: string | null) {
  switch (setor) {
    case "vendas":
      return "Vendas";
    case "financeiro":
      return "Financeiro";
    case "tecnico":
      return "Técnico";
    case "administrativo":
      return "Administrativo";
    default:
      return "";
  }
}

function formatarDataCriacao(data?: string | null) {
  if (!data) return "—";
  const d = new Date(data);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("pt-BR");
}

function getSortValue(usuario: Usuario, field: SortField): string | number {
  switch (field) {
    case "nome":
      return (usuario.nome || "").toLowerCase();
    case "email":
      return (usuario.email || "").toLowerCase();
    case "role":
      return getRoleDisplayName((usuario.role || usuario.user_role) as UserRole).toLowerCase();
    case "setor":
      return getSetorLabel(usuario.setor_departamento).toLowerCase();
    case "status":
      return usuario.ativo ? 1 : 0;
    case "created_at":
      return usuario.created_at ? new Date(usuario.created_at).getTime() : 0;
  }
}

export function UsuariosTable() {
  const { data: usuarios, isLoading, error } = useUsuarios();
  const { canManageUsers } = useAuth();
  const queryClient = useQueryClient();
  const [sort, setSort] = useState<{ field: SortField; direction: SortDirection }>({
    field: "nome",
    direction: "asc",
  });

  const usuariosOrdenados = useMemo(() => {
    const list = [...(usuarios ?? [])];
    list.sort((a, b) => {
      const va = getSortValue(a, sort.field);
      const vb = getSortValue(b, sort.field);
      let cmp = 0;
      if (typeof va === "number" && typeof vb === "number") {
        cmp = va - vb;
      } else {
        cmp = String(va).localeCompare(String(vb), "pt-BR");
      }
      return sort.direction === "asc" ? cmp : -cmp;
    });
    return list;
  }, [usuarios, sort]);

  const toggleSort = (field: SortField) => {
    setSort((prev) =>
      prev.field === field
        ? { field, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { field, direction: "asc" }
    );
  };

  const sortIndicator = (field: SortField) =>
    sort.field === field ? (sort.direction === "asc" ? " ▲" : " ▼") : "";

  if (isLoading) {
    return (
      <Card className="border-0 shadow-md">
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Carregando usuários...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-0 shadow-md">
        <CardContent className="p-8">
          <div className="text-center text-red-600">
            Erro ao carregar usuários: {error.message}
          </div>
        </CardContent>
      </Card>
    );
  }

  const canEditUsers = canManageUsers();

  const handleUsuarioUpdated = () => {
    queryClient.invalidateQueries({ queryKey: ["usuarios"] });
  };

  return (
    <Card className="border-0 shadow-md">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-gray-900">
          <Users className="h-5 w-5 text-green-600" />
          Usuários Cadastrados ({usuarios?.length || 0})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                align="center"
                className="cursor-pointer select-none"
                onClick={() => toggleSort("nome")}
                title="Ordenar"
              >
                Nome{sortIndicator("nome")}
              </TableHead>
              <TableHead
                align="center"
                className="cursor-pointer select-none"
                onClick={() => toggleSort("email")}
                title="Ordenar"
              >
                Email{sortIndicator("email")}
              </TableHead>
              <TableHead
                align="center"
                className="cursor-pointer select-none"
                onClick={() => toggleSort("role")}
                title="Ordenar"
              >
                Role{sortIndicator("role")}
              </TableHead>
              <TableHead
                align="center"
                className="cursor-pointer select-none"
                onClick={() => toggleSort("setor")}
                title="Ordenar"
              >
                Setor{sortIndicator("setor")}
              </TableHead>
              <TableHead
                align="center"
                className="cursor-pointer select-none"
                onClick={() => toggleSort("status")}
                title="Ordenar"
              >
                Status{sortIndicator("status")}
              </TableHead>
              <TableHead
                align="center"
                className="cursor-pointer select-none whitespace-nowrap"
                onClick={() => toggleSort("created_at")}
                title="Ordenar"
              >
                Criado em{sortIndicator("created_at")}
              </TableHead>
              {canEditUsers && <TableHead align="center">Ações</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {usuariosOrdenados.map((usuario) => (
              <TableRow key={usuario.id}>
                <TableCell align="center" className="font-medium">
                  {usuario.nome}
                </TableCell>
                <TableCell align="center">{usuario.email}</TableCell>
                <TableCell align="center">
                  {usuario.role ? (
                    <span
                      className={`inline-block px-2 py-1 rounded-full text-xs ${getRoleBadgeColor(
                        usuario.role as UserRole
                      )}`}
                    >
                      {getRoleDisplayName(usuario.role as UserRole)}
                    </span>
                  ) : (
                    <span className="text-gray-400 text-xs">—</span>
                  )}
                </TableCell>
                <TableCell align="center">
                  {usuario.setor_departamento ? (
                    <span
                      className={`inline-block px-2 py-1 rounded-full text-xs ${getSetorBadgeClass(
                        usuario.setor_departamento
                      )}`}
                    >
                      {getSetorLabel(usuario.setor_departamento)}
                    </span>
                  ) : (
                    <span className="text-gray-400 text-xs">—</span>
                  )}
                </TableCell>
                <TableCell align="center">
                  <span
                    className={`inline-block px-2 py-1 rounded-full text-xs ${
                      usuario.ativo
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {usuario.ativo ? "Ativo" : "Inativo"}
                  </span>
                </TableCell>
                <TableCell align="center">{formatarDataCriacao(usuario.created_at)}</TableCell>
                {canEditUsers && (
                  <TableCell align="center">
                    <div className="flex items-center gap-2 justify-center">
                      <EditarUsuarioDialog
                        usuario={usuario}
                        onUsuarioUpdated={handleUsuarioUpdated}
                      />
                      <RedefinirSenhaDialog
                        usuario={usuario}
                        onSenhaRedefinida={handleUsuarioUpdated}
                      />
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {usuarios && usuarios.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Nenhum usuário encontrado
          </div>
        )}
      </CardContent>
    </Card>
  );
}
